import { ActionType, getType } from 'typesafe-actions';
import * as actions from './actions';
import { SortFolderOptions } from '../../utilities';
import { LayoutOptions } from './utils';

export type LibraryPageActions = ActionType<typeof actions>;

export type LibraryPageState = Readonly<{
  isLoading: boolean;
  lastLoadedDate: string;
  mainSortOptions: SortFolderOptions;
  sideSortOptions: SortFolderOptions;
  searchQuery: string;
  selectedNodeIds: string[];
  error: string;
  layoutOptions: LayoutOptions;
  isManageMode: boolean;
}>;

const getInitialLayoutOptions = (): LayoutOptions => {
  try {
    const stored = localStorage.getItem('libraryLayoutOptions');
    return (stored as LayoutOptions) || 'grid';
  } catch (error) {
    console.warn('Failed to load layout options from localStorage:', error);
    return 'grid';
  }
};

const initialLibraryPageState: LibraryPageState = {
  isLoading: false,
  lastLoadedDate: '',
  mainSortOptions: {
    property: 'name',
    direction: 'asc',
  },
  sideSortOptions: {
    property: 'name',
    direction: 'asc',
  },
  searchQuery: '',
  selectedNodeIds: [],
  error: '',
  layoutOptions: getInitialLayoutOptions(),
  isManageMode: false,
};

export default function libraryPageReducer(
  state = initialLibraryPageState,
  action: LibraryPageActions,
): LibraryPageState {
  switch (action.type) {
    case getType(actions.loadLibraryPageAsync.request):
      return {
        ...state,
        isLoading: true,
        error: '',
      };

    case getType(actions.loadLibraryPageAsync.success):
      return {
        ...state,
        isLoading: false,
        lastLoadedDate: new Date().toISOString(),
      };

    case getType(actions.loadLibraryPageAsync.failure):
      return {
        ...state,
        isLoading: false,
        error: action.payload.message,
      };

    case getType(actions.setMainSortOptions):
      return {
        ...state,
        mainSortOptions: action.payload,
      };

    case getType(actions.setSideSortOptions):
      return {
        ...state,
        sideSortOptions: action.payload,
      };

    case getType(actions.setSearchQuery):
      return {
        ...state,
        searchQuery: action.payload,
      };

    case getType(actions.setSelectedNodeIds):
      return {
        ...state,
        selectedNodeIds: action.payload,
      };

    case getType(actions.clearSelectedNodeIds):
      return {
        ...state,
        selectedNodeIds: [],
      };

    case getType(actions.toggleLayoutOptions):
      const newLayoutOptions = state.layoutOptions === 'grid' ? 'list' : 'grid';

      try {
        localStorage.setItem('libraryLayoutOptions', newLayoutOptions);
      } catch (error) {
        console.warn('Failed to save layout options to localStorage:', error);
      }

      return {
        ...state,
        layoutOptions: newLayoutOptions,
      };

    case getType(actions.setManageMode):
      return {
        ...state,
        isManageMode: action.payload,
      };

    default:
      return state;
  }
}
